import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { Subscription } from '@yoimo/interfaces';
import { firstValueFrom } from 'rxjs';
import { DialogService } from '../../../../shared/dialog.service';
import { MasterChannelService } from '../../../../shared/master-channel.service';
import { UserService } from '../../../../shared/users.service';
import { ValidateService } from '../../../../shared/validate.service';


interface ActiveSubscriptionResult {
  hasActiveSubscription: boolean;
  subscription?: Subscription;
}

@Component({
  selector: 'app-add-subscriptions',
  templateUrl: './add-subscriptions.component.html',
  styleUrls: ['./add-subscriptions.component.scss'],
})

export class AddSubscriptionsComponent implements OnInit {
  @ViewChild('emailInput') emailInput!: ElementRef<HTMLInputElement>;

  selectedPlan: { key: string; value: string } = { key: '', value: '' };
  planDocs!: { key: string; value: string }[];
  isSpinner = false;
  isLoading = true;
  email = "";
  coupon = "";
  isInvalidEmail = false;
  isInvalidUser = false;
  isAlreadyAUser = false;
  docId = '';
  skeletonConfigs = [
    {
      margin: '4px 0 0 23px',
      'border-radius': '5px',
      height: '16px',
      width: '89%'
    },
    {
      margin: '4px 0 0 23px',
      'border-radius': '5px',
      height: '42px',
      width: '89%'
    },
    {
      margin: '10px 0 0 23px',
      'border-radius': '5px',
      height: '16px',
      width: '89%'
    },
    {
      margin: '4px 0 0 23px',
      'border-radius': '5px',
      height: '42px',
      width: '89%'
    }
  ];

  constructor(
    private readonly dialogRef: MatDialogRef<AddSubscriptionsComponent>,
    private readonly masterChannelService: MasterChannelService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      channelDocId: string;
    },
    private readonly dialogService: DialogService,
    private readonly validateService: ValidateService,
    private readonly userService: UserService,
  ) { }

  ngOnInit(): void {
    this.getPlans();
    this.isLoading = false;
  }

  async getPlans() {
    if (!this.data.channelDocId) {return;}
    const planDocs = await this.masterChannelService.getPlanSource(
      this.data.channelDocId
    );
    this.planDocs = planDocs.docs
      .filter((plan) => !plan.get('archived'))
      .map((plan) => ({ 
        key: plan.get('name'), 
        value: plan.id 
      }));
  }

  onPlanSelected(selectedPlan: { key: string; value: string }) {
    this.selectedPlan = selectedPlan;
  }

  onPlanCleared() {
    this.selectedPlan = { key: '', value: '' };
  }

  validateEmailFormat() {
    this.isInvalidEmail= !this.validateService.validateEmail(this.email);
  }

  private async checkActiveInternalSubscription(userId: string): Promise<ActiveSubscriptionResult> {
    try {
      const existingSubscriptions = await this.masterChannelService.getUserSubscriptions(
        this.data.channelDocId, 
        userId
      );

      const currentDate = new Date();
      
      const activeSubscription = existingSubscriptions.find(subscription => 
        this.isActiveInternalSubscription(subscription, currentDate)
      );

      return {
        hasActiveSubscription: !!activeSubscription,
        subscription: activeSubscription
      };
    } catch (error) {
      console.error('Error checking active subscriptions:', error);
      return {
        hasActiveSubscription: false
      };
    }
  }

  private isActiveInternalSubscription(subscription: any, currentDate: Date): boolean {
    if (subscription.status !== 'ACTIVE') {
      return false;
    }

    const creationDate = this.parseCreationDate(subscription.creationDate);
    if (!creationDate) {
      return false;
    }

    const periodicity = subscription.selectedConfig?.periodicity?.toLowerCase();
    const expiryDate = this.calculateExpiryDate(creationDate, periodicity);
    
    return currentDate < expiryDate;
  }

  private parseCreationDate(creationDate: any): Date | null {
    if (creationDate instanceof Timestamp) {
      return creationDate.toDate();
    }
    
    if (creationDate instanceof Date) {
      return creationDate;
    }
    
    if (typeof creationDate === 'object' && 'toDate' in creationDate) {
      return (creationDate as Timestamp).toDate();
    }
    
    console.warn('Invalid creationDate format:', creationDate);
    return null;
  }

  private calculateExpiryDate(creationDate: Date, periodicity: string): Date {
    const expiryDate = new Date(creationDate);
    
    switch (periodicity) {
      case 'yearly':
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        break;
      case 'monthly':
        expiryDate.setMonth(expiryDate.getMonth() + 1);
        break;
      case 'weekly':
        expiryDate.setDate(expiryDate.getDate() + 7);
        break;
      case 'daily':
        expiryDate.setDate(expiryDate.getDate() + 1);
        break;
      default:
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        break;
    }
    
    return expiryDate;
  }

  closeDialog(): void {
    return this.dialogRef.close();
  }

  convertCouponToUpperCase() {
    if (this.coupon) {
      this.coupon = this.coupon.toUpperCase();
    }
  }


  async createSubscription() {
    if (this.isSpinner) {
      return;
    }
    if (!globalThis.navigator.onLine) {
      this.dialogService.openInternetLostDialog();
      return;
    }

    this.isSpinner = true;

    try {
      const userExists = await firstValueFrom(this.userService.getUserByEmail(this.email));
      
      if (userExists.length === 0) {
        this.isSpinner = false;
        this.dialogRef.close();
        this.dialogService.openFailureDialog({
          header: 'Failed',
          failureMessage: 'User not registered',
          showClose: false
        });
        return;
      }

      const userId = userExists[0].docId;
      
      if (!userId) {
        this.isSpinner = false;
        this.dialogRef.close();
        this.dialogService.openFailureDialog({
          header: 'Failed',
          failureMessage: 'User ID not found',
          showClose: false
        });
        return;
      }

      const result = await this.checkActiveInternalSubscription(userId);
    
      if (result.hasActiveSubscription) {
        this.isSpinner = false;
        this.dialogRef.close();
        
        const subscriptionId = result.subscription?.subscriptionId;
        const isInternalSubscription = subscriptionId?.startsWith('INTERNAL');
        
        let failureMessage = '';
        if (isInternalSubscription) {
          failureMessage = 'The user has an internal subscription already';
        } else {
          failureMessage = `The user has a paid subscription through ${subscriptionId?.split(':')[0]}`;
        }
        
        this.dialogService.openFailureDialog({
          header: "Failed to create internal subscription",
          failureMessage: failureMessage,
          showClose: false
        });
        return;
      }

      const now = new Date();
      const subscription: Subscription = {
        coupon: this.coupon.toUpperCase(),
        creationDate: now,
        lastTimeUsed: now,
        location: null,
        planId: this.selectedPlan.value, 
        selectedConfig: {
          currency: 'gbp',
          periodicity: 'YEARLY'
        },
        status: 'ACTIVE',
        subscriptionId: 'INTERNAL:0:1',
        userId: userId,
      };

      await this.masterChannelService.addSubscription(this.data.channelDocId, subscription);
      this.isSpinner = false;
      this.dialogRef.close('subscription_created');
    } catch (error) {
      this.isSpinner = false;
      this.dialogRef.close();
      this.dialogService.openFailureDialog({
        header: 'Failed',
        failureMessage: 'Failed to create subscription. Please try again.'
      });
      console.error('Error creating subscription:', error);
    }
  }

  disableCreate() {
    return !this.email || !this.selectedPlan.value || !this.coupon || this.isInvalidEmail;
  }
}
